import { useMemo } from 'react'

/**
 * A faint, fixed star field behind the whole page.
 *
 * Positions come from a seeded generator so they stay put between renders,
 * and the layer is `pointer-events-none` + `aria-hidden` — decoration only.
 * The `prefers-reduced-motion` rule in the base stylesheet stops the twinkle.
 */
function seededRandom(seed: number) {
  let state = seed
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296
    return state / 4294967296
  }
}

export function StarField({ count = 70 }: { count?: number }) {
  const stars = useMemo(() => {
    const random = seededRandom(20270802)
    return Array.from({ length: count }, (_, index) => ({
      id: index,
      top: random() * 100,
      left: random() * 100,
      size: 1 + random() * 1.6,
      delay: random() * 4,
    }))
  }, [count])

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* Deep-space wash */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_-10%,#1b1b20_0%,var(--color-ink)_62%)]" />
      {stars.map((star) => (
        <span
          key={star.id}
          className="absolute animate-[var(--animate-twinkle)] rounded-full bg-parchment"
          style={{
            top: `${star.top}%`,
            left: `${star.left}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            animationDelay: `${star.delay}s`,
          }}
        />
      ))}
    </div>
  )
}
